import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { reportCardAPI, academicAPI, studentAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';

const extractCollection = (responseOrPayload) => {
  const payload = responseOrPayload?.data !== undefined ? responseOrPayload.data : responseOrPayload;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getErrorMessage = (error, fallback = 'Operation failed') => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback
);

const getCategoryName = (category) => category?.category_name || category?.name || 'Unknown Category';
const getCategoryCode = (category) => category?.category_code || category?.code || '-';
const getRatingName = (rating) => rating?.rating_name || rating?.name || 'Unknown Rating';
const getRatingCode = (rating) => rating?.rating_code || rating?.code || '-';
const getTermName = (term) => term?.name || term?.term_name || 'Unknown Term';
const getStudentName = (student) => (
  student?.full_name ||
  `${student?.last_name || ''} ${student?.first_name || ''}`.trim() ||
  student?.registration_no ||
  `Student #${student?.id}`
);
const getAcademicYearFromTerm = (term) => (
  Number(term?.academic_year || term?.year || new Date().getFullYear())
);

const INITIAL_CATEGORY_FORM = {
  category_code: '',
  category_name: '',
  name: '',
  display_order: '',
  description: ''
};
const INITIAL_RATING_FORM = {
  rating_code: '',
  rating_name: '',
  name: '',
  rating_value: '',
  value: '',
  description: ''
};
const INITIAL_CONDUCT_FORM = { student: null, term: null, category: null, rating: null, remarks: '' };

const responsiveAutocompleteSx = {
  width: '100%',
  minWidth: {
    sm: 240,
    lg: 280
  }
};

export default function ConductManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [categories, setCategories] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [studentConduct, setStudentConduct] = useState([]);
  const [terms, setTerms] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [categoryDialog, setCategoryDialog] = useState(false);
  const [ratingDialog, setRatingDialog] = useState(false);
  const [conductDialog, setConductDialog] = useState(false);

  const [selectedTermFilter, setSelectedTermFilter] = useState(null);
  const [selectedStudentFilter, setSelectedStudentFilter] = useState(null);

  const [categoryForm, setCategoryForm] = useState(INITIAL_CATEGORY_FORM);
  const [ratingForm, setRatingForm] = useState(INITIAL_RATING_FORM);
  const [conductForm, setConductForm] = useState(INITIAL_CONDUCT_FORM);

  const fetchBaseData = useCallback(async () => {
    setLoadingPage(true);
    try {
      const [catRes, ratRes, termRes, studentRes] = await Promise.all([
        reportCardAPI.getConductCategories(),
        reportCardAPI.getConductRatings(),
        academicAPI.getTerms(),
        studentAPI.getActive({ per_page: 1000 })
      ]);

      setCategories(extractCollection(catRes));
      setRatings(extractCollection(ratRes));
      setTerms(extractCollection(termRes));
      setStudents(extractCollection(studentRes));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load setup data'));
    } finally {
      setLoadingPage(false);
    }
  }, []);

  const loadStudentConduct = useCallback(async () => {
    setLoadingRecords(true);
    try {
      if (selectedStudentFilter?.id && selectedTermFilter?.id) {
        const response = await reportCardAPI.getStudentConductForStudent(selectedStudentFilter.id, {
          term_id: selectedTermFilter.id,
          academic_year: getAcademicYearFromTerm(selectedTermFilter)
        });
        setStudentConduct(extractCollection(response));
        return;
      }

      const response = await reportCardAPI.getStudentConduct();
      setStudentConduct(extractCollection(response));
    } catch (err) {
      setStudentConduct([]);
      setError(getErrorMessage(err, 'Failed to load conduct records'));
    } finally {
      setLoadingRecords(false);
    }
  }, [selectedStudentFilter, selectedTermFilter]);

  useEffect(() => {
    fetchBaseData();
  }, [fetchBaseData]);

  useEffect(() => {
    loadStudentConduct();
  }, [loadStudentConduct]);

  const filteredRecords = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return studentConduct;

    return studentConduct.filter((record) => {
      const student = getStudentName(record?.student).toLowerCase();
      const term = getTermName(record?.term).toLowerCase();
      const category = getCategoryName(record?.category || record?.conduct_category).toLowerCase();
      const rating = getRatingName(record?.rating || record?.conduct_rating).toLowerCase();
      const remarks = String(record?.remarks || '').toLowerCase();
      return (
        student.includes(normalizedSearch) ||
        term.includes(normalizedSearch) ||
        category.includes(normalizedSearch) ||
        rating.includes(normalizedSearch) ||
        remarks.includes(normalizedSearch)
      );
    });
  }, [searchTerm, studentConduct]);

  const handleCreateCategory = async () => {
    const categoryName = (categoryForm.category_name || categoryForm.name || '').trim();
    if (!categoryName) {
      setError('Category name is required');
      return;
    }

    const categoryCode = (categoryForm.category_code || categoryName)
      .toUpperCase()
      .replace(/\s+/g, '_')
      .slice(0, 20);

    try {
      await reportCardAPI.createConductCategory({
        category_code: categoryCode,
        category_name: categoryName,
        name: categoryName,
        display_order: categoryForm.display_order === '' ? null : Number(categoryForm.display_order),
        description: categoryForm.description || null,
        is_active: true
      });
      setSuccess('Category created successfully');
      setCategoryDialog(false);
      setCategoryForm(INITIAL_CATEGORY_FORM);
      await fetchBaseData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create category'));
    }
  };

  const handleCreateRating = async () => {
    const ratingName = (ratingForm.rating_name || ratingForm.name || '').trim();
    const rawRatingValue = ratingForm.rating_value !== '' ? ratingForm.rating_value : ratingForm.value;
    if (!ratingName || rawRatingValue === '') {
      setError('Rating name and value are required');
      return;
    }

    const ratingCode = (ratingForm.rating_code || ratingName)
      .toUpperCase()
      .replace(/\s+/g, '_')
      .slice(0, 20);

    try {
      await reportCardAPI.createConductRating({
        rating_code: ratingCode,
        rating_name: ratingName,
        name: ratingName,
        rating_value: Number(rawRatingValue),
        value: Number(rawRatingValue),
        description: ratingForm.description || null,
        is_active: true
      });
      setSuccess('Rating created successfully');
      setRatingDialog(false);
      setRatingForm(INITIAL_RATING_FORM);
      await fetchBaseData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create rating'));
    }
  };

  const handleCreateConduct = async () => {
    if (!conductForm.student?.id || !conductForm.term?.id || !conductForm.category?.id || !conductForm.rating?.id) {
      setError('Student, term, category, and rating are required');
      return;
    }

    const academicYear = getAcademicYearFromTerm(conductForm.term);

    try {
      await reportCardAPI.createStudentConduct({
        student_id: conductForm.student.id,
        term_id: conductForm.term.id,
        academic_year: academicYear,
        conduct_category_id: conductForm.category.id,
        conduct_rating_id: conductForm.rating.id,
        category_id: conductForm.category.id,
        rating_id: conductForm.rating.id,
        remarks: conductForm.remarks || ''
      });
      setSuccess('Conduct record created successfully');
      setConductDialog(false);
      setConductForm(INITIAL_CONDUCT_FORM);
      await loadStudentConduct();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create conduct record'));
    }
  };

  return (
    <Box>
      <ModulePageHeader
        title="Conduct Management"
        subtitle="Create categories, ratings, and student conduct records with searchable filters"
        actions={[
          <Button
            key="refresh"
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchBaseData();
              loadStudentConduct();
            }}
            disabled={loadingPage || loadingRecords}
            fullWidth={isMobile}
          >
            Refresh
          </Button>,
          <Button
            key="add-record"
            variant="contained"
            startIcon={<Add />}
            onClick={() => setConductDialog(true)}
            fullWidth={isMobile}
          >
            Add Conduct Record
          </Button>
        ]}
      />

      {error ? <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Conduct Categories</Typography>
                <Button startIcon={<Add />} variant="outlined" onClick={() => setCategoryDialog(true)}>
                  Add Category
                </Button>
              </Box>
              {loadingPage ? (
                <Box py={3} textAlign="center">
                  <CircularProgress size={24} />
                </Box>
              ) : categories.length === 0 ? (
                <EmptyRecordsState message="No categories found." />
              ) : isMobile ? (
                <ResponsiveRecordList
                  items={categories}
                  renderItem={(category) => (
                    <Stack spacing={0.75}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight={700}>{getCategoryName(category)}</Typography>
                        <Chip size="small" label={getCategoryCode(category)} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{category.description || 'No description'}</Typography>
                    </Stack>
                  )}
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categories.map((cat) => (
                        <TableRow key={cat.id}>
                          <TableCell>{getCategoryCode(cat)}</TableCell>
                          <TableCell>{getCategoryName(cat)}</TableCell>
                          <TableCell>{cat.display_order ?? '-'}</TableCell>
                          <TableCell>{cat.description || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Conduct Ratings</Typography>
                <Button startIcon={<Add />} variant="outlined" onClick={() => setRatingDialog(true)}>
                  Add Rating
                </Button>
              </Box>
              {loadingPage ? (
                <Box py={3} textAlign="center">
                  <CircularProgress size={24} />
                </Box>
              ) : ratings.length === 0 ? (
                <EmptyRecordsState message="No ratings found." />
              ) : isMobile ? (
                <ResponsiveRecordList
                  items={ratings}
                  renderItem={(rating) => (
                    <Stack spacing={0.75}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight={700}>{getRatingName(rating)}</Typography>
                        <Chip label={rating.rating_value || rating.value || '-'} size="small" />
                      </Box>
                      <Typography variant="caption" color="text.secondary">Code: {getRatingCode(rating)}</Typography>
                      <Typography variant="body2" color="text.secondary">{rating.description || 'No description'}</Typography>
                    </Stack>
                  )}
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Value</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ratings.map((rating) => (
                        <TableRow key={rating.id}>
                          <TableCell>{getRatingCode(rating)}</TableCell>
                          <TableCell>{getRatingName(rating)}</TableCell>
                          <TableCell><Chip label={rating.rating_value || rating.value || '-'} size="small" /></TableCell>
                          <TableCell>{rating.description || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Student Conduct Records</Typography>
                <Button startIcon={<Add />} variant="contained" onClick={() => setConductDialog(true)}>
                  Add Record
                </Button>
              </Box>

              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    fullWidth
                    sx={responsiveAutocompleteSx}
                    options={terms}
                    value={selectedTermFilter}
                    onChange={(_, value) => setSelectedTermFilter(value)}
                    getOptionLabel={(option) => getTermName(option)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="Filter by Term" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    fullWidth
                    sx={responsiveAutocompleteSx}
                    options={students}
                    value={selectedStudentFilter}
                    onChange={(_, value) => setSelectedStudentFilter(value)}
                    getOptionLabel={(option) => getStudentName(option)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="Filter by Student" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Records"
                    placeholder="Search remarks, category, rating, student..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Grid>
              </Grid>

              {loadingRecords ? (
                <Box py={4} textAlign="center">
                  <CircularProgress size={28} />
                </Box>
              ) : filteredRecords.length === 0 ? (
                <EmptyRecordsState message="No conduct records found for selected filters." />
              ) : isMobile ? (
                <ResponsiveRecordList
                  items={filteredRecords}
                  renderItem={(record) => (
                    <Stack spacing={1}>
                      <Typography fontWeight={700}>{getStudentName(record.student)}</Typography>
                      <Typography variant="body2" color="text.secondary">{getTermName(record.term)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getCategoryName(record.category || record.conduct_category)} | {getRatingName(record.rating || record.conduct_rating)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{record.remarks || 'No remarks'}</Typography>
                    </Stack>
                  )}
                />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Term</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Rating</TableCell>
                        <TableCell>Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecords.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell>{getStudentName(record.student)}</TableCell>
                          <TableCell>{getTermName(record.term)}</TableCell>
                          <TableCell>{getCategoryName(record.category || record.conduct_category)}</TableCell>
                          <TableCell><Chip label={getRatingName(record.rating || record.conduct_rating)} size="small" /></TableCell>
                          <TableCell>{record.remarks || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={categoryDialog} onClose={() => setCategoryDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add Conduct Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category Code"
            value={categoryForm.category_code}
            onChange={(e) => setCategoryForm({ ...categoryForm, category_code: e.target.value })}
            margin="normal"
            placeholder="e.g. ATTEND"
          />
          <TextField
            fullWidth
            label="Category Name"
            value={categoryForm.category_name}
            onChange={(e) => setCategoryForm({ ...categoryForm, category_name: e.target.value, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Display Order"
            type="number"
            value={categoryForm.display_order}
            onChange={(e) => setCategoryForm({ ...categoryForm, display_order: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={categoryForm.description}
            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ratingDialog} onClose={() => setRatingDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add Conduct Rating</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Rating Code"
            value={ratingForm.rating_code}
            onChange={(e) => setRatingForm({ ...ratingForm, rating_code: e.target.value })}
            margin="normal"
            placeholder="e.g. EXC"
          />
          <TextField
            fullWidth
            label="Rating Name"
            value={ratingForm.rating_name}
            onChange={(e) => setRatingForm({ ...ratingForm, rating_name: e.target.value, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Value"
            type="number"
            value={ratingForm.rating_value}
            onChange={(e) => setRatingForm({ ...ratingForm, rating_value: e.target.value, value: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={ratingForm.description}
            onChange={(e) => setRatingForm({ ...ratingForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatingDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateRating} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={conductDialog} onClose={() => setConductDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add Conduct Record</DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={students}
            value={conductForm.student}
            onChange={(_, value) => setConductForm({ ...conductForm, student: value })}
            getOptionLabel={(option) => getStudentName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Student" margin="normal" size="small" required />}
          />
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={terms}
            value={conductForm.term}
            onChange={(_, value) => setConductForm({ ...conductForm, term: value })}
            getOptionLabel={(option) => getTermName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Term" margin="normal" size="small" required />}
          />
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={categories}
            value={conductForm.category}
            onChange={(_, value) => setConductForm({ ...conductForm, category: value })}
            getOptionLabel={(option) => getCategoryName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Category" margin="normal" size="small" required />}
          />
          <Autocomplete
            fullWidth
            sx={responsiveAutocompleteSx}
            options={ratings}
            value={conductForm.rating}
            onChange={(_, value) => setConductForm({ ...conductForm, rating: value })}
            getOptionLabel={(option) => getRatingName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Rating" margin="normal" size="small" required />}
          />
          <TextField
            fullWidth
            label="Remarks"
            value={conductForm.remarks}
            onChange={(e) => setConductForm({ ...conductForm, remarks: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConductDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateConduct} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

